/**
 * SecondaryPanelContent Component
 * Standardized content wrapper with header, breadcrumb support, close button, scrollable content area
 *
 * Features:
 * - Standardized header with title and close button
 * - Breadcrumb navigation support (for nested views)
 * - "Open" button support (ArrowUpRight icon, opens in main editor)
 * - Scrollable content area
 * - Loading and error states
 */

import { X, ChevronRight, ArrowUpRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { SecondaryPanelContentProps } from './secondary-panel-types'
import { cn } from '@/lib/utils'

export function SecondaryPanelContent({
  title,
  breadcrumbs,
  onClose,
  headerActions,
  children,
  className,
  loading = false,
  error = null,
}: SecondaryPanelContentProps) {
  const hasCrumbs = Boolean(breadcrumbs && breadcrumbs.length > 0)

  return (
    <div className={cn('flex flex-col h-full bg-card', className)}>
      {/* Header - same h-11 row as PreviewPaneHeader `row` */}
      <div className="flex h-11 min-w-0 flex-shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {hasCrumbs ? (
            <nav
              className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden text-xs text-muted-foreground"
              aria-label="Panel breadcrumb"
            >
              {breadcrumbs!.map((crumb, index) => {
                const isLast = index === breadcrumbs!.length - 1
                return (
                  <div
                    key={`${crumb.label}-${index}`}
                    className={cn(
                      'flex items-center gap-1',
                      isLast ? 'min-w-0 flex-1 overflow-hidden' : 'shrink-0',
                    )}
                  >
                    {index > 0 ? (
                      <ChevronRight
                        className="h-3 w-3 shrink-0 text-muted-foreground"
                        strokeWidth={2.25}
                        aria-hidden
                      />
                    ) : null}
                    {crumb.onClick && !isLast ? (
                      <button
                        type="button"
                        onClick={crumb.onClick}
                        className="shrink-0 hover:text-foreground hover:underline underline-offset-2 transition-colors"
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span
                        className={cn(
                          'truncate',
                          isLast ? 'min-w-0 text-foreground/80' : 'shrink-0',
                        )}
                        title={crumb.label}
                      >
                        {crumb.label}
                      </span>
                    )}
                  </div>
                )
              })}
            </nav>
          ) : title ? (
            <h2 className="min-w-0 truncate text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </h2>
          ) : null}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {headerActions}
          {onClose ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Close"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">{children}</div>
      )}
    </div>
  )
}

/**
 * Helper component for "Open" button with ArrowUpRight icon
 */
export function SecondaryPanelOpenButton({
  onClick,
  label,
  title,
}: {
  onClick: () => void
  /** Tooltip / aria; defaults to "Open in tab" or label when provided */
  label?: string
  title?: string
}) {
  const resolvedTitle = title ?? label ?? 'Open in tab'
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      title={resolvedTitle}
      onClick={onClick}
    >
      <ArrowUpRight className="h-4 w-4" />
    </Button>
  )
}
